import * as React from "react";

import { useResizeDetector } from "react-resize-detector";

import { Box, Divider, makeStyles, Theme, Typography } from "@material-ui/core";

import { IPageProps } from "../IPageProps";

import { IHttpRequest, Status, useHttp } from "Utility/useHttp";

import { InfoButton } from "./InfoButton";
import { ShareButton } from "./ShareButton";
import { IButtonProps } from "./IButtonProps";
import { RandomButton } from "./RandomButton";
import { FavoriteButton} from "./FavoriteButton";
import { SettingsButton } from "./SettingsButton";
import { DownloadButton } from "./DownloadButton";

import { Page } from "../Page";
import { QuiltCanvas } from "Components/QuiltCanvas";
import { IQuiltRenderData } from "Model/IQuiltRenderData";

// https://github.com/mui-org/material-ui/issues/17010
declare module '@material-ui/core/Box' {
    interface BoxProps {
        ref?: React.MutableRefObject<HTMLElement | undefined>;
    }
}

const useStyles = makeStyles((theme: Theme) => ({
    divider: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
    },
}));

export function HomePage(props: IPageProps): React.ReactElement {
    const classes = useStyles();

    const [request, setRequest] = React.useState<IHttpRequest | null>(null);
    const quiltLoadState = useHttp<IQuiltRenderData>(request);

    const { width, height, ref } = useResizeDetector();

    const quiltImageContainerStyle: React.CSSProperties = {
        width: "100%",
        minWidth: `${Math.floor(576 / window.devicePixelRatio)}px`,
        display: "flex",
        justifyContent: "center",
        alignItems: "top"
    };

    const renderData = quiltLoadState.status === Status.Complete ? quiltLoadState.data : null;

    const baseButtonProps: IButtonProps = {
        disabled: quiltLoadState.status === Status.Loading,

        user: props.user,
    
        config: props.config,
        params: props.params,
        setParams: props.setParams,
    
        quiltRenderData: renderData
    };

    const needQuiltButtonProps = {
        ...baseButtonProps,
        disabled: baseButtonProps.disabled || quiltLoadState.status === Status.Error
    }

    React.useEffect(() => {
        setRequest({
            method: "post",
            url: "/api/quilt/generate",
            data: props.params
        })
    },
    [props.params]);

    return (
        <Page>
            <Box display="flex">
                <SettingsButton {...needQuiltButtonProps} />
                
                <Divider orientation="vertical" flexItem className={classes.divider}/>
                <InfoButton {...needQuiltButtonProps} />
                
                <Divider orientation="vertical" flexItem className={classes.divider}/>
                <ShareButton {...needQuiltButtonProps} />

                <Divider orientation="vertical" flexItem className={classes.divider}/>
                <DownloadButton {...needQuiltButtonProps} />

                {props.user !== undefined && (
                    <>
                        <Divider orientation="vertical" flexItem className={classes.divider}/>
                        <FavoriteButton {...needQuiltButtonProps} />
                    </>
                )}

                <Divider orientation="vertical" flexItem className={classes.divider}/>
                <RandomButton {...baseButtonProps} />
            </Box>
            <Box mt={1} mb={1} flexGrow={1} style={quiltImageContainerStyle} ref={ref}>
                {width && height && renderData && 
                    <QuiltCanvas renderData={renderData} width={width} height={height} />
                }
                {quiltLoadState.status === Status.Error &&
                    <Typography color="error">Failed to load quilt.</Typography>
                }
            </Box>
        </Page>
    )
}